import { Dispatch } from '@reduxjs/toolkit';
import { keyBy, union } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { AccountType } from 'dashboard/models/account-type';
import { Case, CaseMeta, CaseObject } from 'dashboard/models/case';
import { CaseComment } from 'dashboard/models/case-comment';
import { CaseMessage } from 'dashboard/models/case-message';
import { CaseQueryResult } from 'dashboard/models/case-query-result';
import { CaseTemplate } from 'dashboard/models/case-template';
import { Device } from 'dashboard/models/device';
import { Document } from 'dashboard/models/document';
import { Environment } from 'dashboard/models/environment';
import { GraphSnapshot } from 'dashboard/models/graph-snapshot';
import { Inquiry } from 'dashboard/models/inquiry';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryShowViewInquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { List } from 'dashboard/models/list';
import { ListItem } from 'dashboard/models/list-item';
import { ListMatch } from 'dashboard/models/list-match';
import { Report } from 'dashboard/models/report';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import { TriggerCase } from 'dashboard/models/trigger';
import { User } from 'dashboard/models/user';
import { ALL_VERIFICATION_API_MODEL_TYPES, Verification } from 'dashboard/models/verification';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import {
  setCase,
  setCaseDataManager,
  setInquiriesDetails,
  setReportsDetails,
} from 'dashboard/redux/slices/case';
import { ApiOptions } from 'lib/clients/ApiClient';
import {
  ApiEndpoint,
  ApiListResponse,
  ApiModel,
  ApiSingletonPayload,
  PaginationParams,
} from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

export function loadCase({
  id,
  currentEnvironment,
  currentUser,
  isPollRequest = false,
}: {
  id: string;
  currentEnvironment: Environment;
  currentUser: User;
  isPollRequest?: boolean;
}) {
  return async (dispatch: Dispatch) => {
    const options: ApiOptions = {};
    if (isPollRequest) {
      options.headers = {
        'Persona-Skip-Log-User-Action': 'true',
      };
    }

    const caseResponse = await SessionClientDashboard.getResource<Case, CaseMeta>(
      ApiEndpoint.Case,
      id,
      options,
    );

    const kase = caseResponse.data;
    const includedItemsMap = keyBy('id', caseResponse.included);
    const caseIncludedItems = caseResponse.included ?? [];

    const caseComments = caseIncludedItems.filter(
      (object) => object.type === ApiModel.CaseComment,
    ) as CaseComment[];
    const commenterIds = caseComments.map((comment) => comment.relationships.commenter.data?.id);

    // TODO: (melissa) remove this when we've backfilled everything to workflow run ids
    const commentWorkflowStepActions = caseIncludedItems.filter((object) => {
      return object.type === ApiModel.WorkflowRunStepAction && commenterIds.includes(object.id);
    }) as WorkflowRunStepAction[];

    const commentWorkflowRunIds = caseIncludedItems
      .filter((object) => {
        return object.type === ApiModel.WorkflowRun && commenterIds.includes(object.id);
      })
      .map((object) => object.id);

    const allCommentWorkflowRunIds = union(
      commentWorkflowRunIds,
      commentWorkflowStepActions.map((action) => action.relationships['workflow-run'].data.id),
    );

    let commentWorkflowRuns: WorkflowRun[] = [];
    let commentWorkflows: Workflow[] = [];
    if (
      currentUser.attributes.permissions?.includes(Permission.WorkflowRunList) &&
      allCommentWorkflowRunIds.length > 0
    ) {
      const commentWorkflowRunsResponse = await SessionClientDashboard.listResource<
        WorkflowRun,
        PaginationParams
      >(ApiEndpoint.WorkflowRun, {
        ...options,
        filter: {
          'environment-id': currentEnvironment.id,
          'workflow-run-ids': allCommentWorkflowRunIds,
        },
        page: {
          size: 100, // same page size as event timeline
        },
      });

      commentWorkflowRuns = commentWorkflowRunsResponse.data;
      commentWorkflows = (commentWorkflowRunsResponse.included ?? []).filter(
        (object) => object.type === ApiModel.Workflow,
      ) as Workflow[];
    }

    let creator;
    switch (kase.relationships.creator.data?.type) {
      case ApiModel.WorkflowRun:
        creator = includedItemsMap[kase.relationships.creator.data.id] as WorkflowRun;
        break;
      case ApiModel.User:
        creator = includedItemsMap[kase.relationships.creator.data.id] as User;
        break;
      default:
        creator = undefined;
    }

    const assignee =
      kase.relationships.assignee.data != null
        ? (includedItemsMap[kase.relationships.assignee.data.id] as User)
        : undefined;

    let resolver;
    switch (kase.relationships.resolver.data?.type) {
      case ApiModel.WorkflowRun:
        resolver = includedItemsMap[kase.relationships.resolver.data.id] as WorkflowRun;
        break;
      case ApiModel.User:
        resolver = includedItemsMap[kase.relationships.resolver.data.id] as User;
        break;
      default:
        resolver = undefined;
    }

    let updater;
    switch (kase.relationships.updater.data?.type) {
      case ApiModel.WorkflowRun:
        updater = includedItemsMap[kase.relationships.updater.data.id] as WorkflowRun;
        break;
      case ApiModel.User:
        updater = includedItemsMap[kase.relationships.updater.data.id] as User;
        break;
      default:
        updater = undefined;
    }

    const caseTemplate =
      kase.relationships['case-template'].data != null
        ? (includedItemsMap[kase.relationships['case-template'].data.id] as CaseTemplate)
        : undefined;

    // tags returned by the case endpoint include tags associated with inquiry and txn objects within a case
    // along with tags directly associated case
    const tags = caseIncludedItems.filter((object) => object.type === ApiModel.Tag) as Tag[];

    const caseTags = tags.filter((tag) =>
      kase.relationships.tags.data.map((caseTag) => caseTag.id).includes(tag.id),
    );

    const caseMessages = caseIncludedItems.filter(
      (object) => object.type === ApiModel.CaseMessage,
    ) as CaseMessage[];

    const caseQueryResults = caseIncludedItems.filter(
      (object) => object.type === ApiModel.CaseQueryResult,
    ) as CaseQueryResult[];

    const objects = kase.relationships.objects.data.map((object) =>
      includedItemsMap[object.id] ? includedItemsMap[object.id] : object,
    ) as CaseObject[]; // This typing needs to be improved since SARs will only have an id and not the full data model.

    const verifications = caseIncludedItems.filter((object) =>
      ALL_VERIFICATION_API_MODEL_TYPES.includes(object.type),
    ) as Verification[];

    const documents = caseIncludedItems.filter((object) => {
      switch (object.type) {
        case ApiModel.DocumentGeneric:
        case ApiModel.DocumentGovernmentId:
          return true;
        default:
          return false;
      }
    }) as Document[];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const actionWorkflowIds = caseTemplate.relationships.workflows.data.map(
      (workflow) => workflow.id,
    );

    const actionWorkflowsData = actionWorkflowIds.map((id) => includedItemsMap[id]) as Workflow[];

    const actionWorkflows = actionWorkflowsData
      .map((workflow) => [workflow, workflow.relationships['latest-published-version'].data.id])
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      .map(([workflow, versionId]: [Workflow, string]) => [
        workflow,
        includedItemsMap[versionId] as WorkflowVersion,
      ])
      .filter(([_workflow, workflowVersion]) => workflowVersion != null)
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      .map(([workflow, workflowVersion]: [Workflow, WorkflowVersion]) => {
        return {
          workflow,
          trigger: includedItemsMap[workflowVersion.relationships.trigger.data.id] as TriggerCase,
        };
      });

    const transactionStatuses = caseIncludedItems.filter(
      (object) => object.type === ApiModel.TransactionStatus,
    ) as TransactionStatus[];

    const transactionTypes = caseIncludedItems.filter(
      (object) => object.type === ApiModel.TransactionType,
    ) as TransactionType[];

    const accountTypes = caseIncludedItems.filter(
      (object) => object.type === ApiModel.AccountType,
    ) as AccountType[];

    const graphSnapshots = caseIncludedItems.filter(
      (object) => object.type === ApiModel.GraphSnapshot,
    ) as GraphSnapshot[];

    const caseDetails = {
      accountTypes,
      actionWorkflows,
      assignee,
      caseComments,
      caseMessages,
      caseQueryResults,
      caseTags,
      caseTemplate,
      commentWorkflowStepActions,
      commentWorkflows,
      commentWorkflowRuns,
      creator,
      documents,
      graphSnapshots,
      objects,
      resolver,
      tags,
      transactionStatuses,
      transactionTypes,
      updater,
      verifications,
    };

    dispatch(setCase({ key: kase.id, case: kase, caseDetails: caseDetails }));
    if (caseResponse.meta?.['case-data-manager']) {
      dispatch(
        setCaseDataManager({
          key: kase.id,
          caseDataManager: caseResponse.meta['case-data-manager'],
        }),
      );
    }
  };
}

export const loadCaseInquiries = ({ id }: { id: string }) => {
  return async (dispatch: Dispatch) => {
    const inquiriesResponse = await SessionClientDashboard.get<ApiListResponse<Inquiry>>(
      ApiEndpoint.Case,
      id,
      'inquiry-objects',
    );

    const inquiries = inquiriesResponse.data;
    const includedItemsMap = keyBy('id', inquiriesResponse.included);
    const included = inquiriesResponse.included || [];

    const templates = included.filter((object) => object.type === ApiModel.Template) as Template[];

    const inquiryTemplates = included.filter(
      (object) => object.type === ApiModel.InquiryTemplate,
    ) as InquiryTemplate[];

    const inquiryTemplateVersions = included.filter(
      (object) => object.type === ApiModel.InquiryTemplateVersion,
    ) as InquiryShowViewInquiryTemplateVersion[];

    const tags = included.filter((object) => object.type === ApiModel.Tag) as Tag[];

    const listMatches = included.filter(
      (object) => object.type === ApiModel.ListMatch,
    ) as ListMatch[];

    const lists = listMatches.map(
      (lm) => includedItemsMap[lm.relationships.list.data.id],
    ) as List[];

    const listItems = listMatches.map(
      (lm) => includedItemsMap[lm.relationships['list-item'].data.id],
    ) as ListItem[];

    const sessions = included.filter(
      (object) => object.type === ApiModel.InquirySession,
    ) as InquirySession[];

    const verifications = included.filter((object) =>
      ALL_VERIFICATION_API_MODEL_TYPES.includes(object.type),
    ) as Verification[];

    const devices = included.filter((object) => object.type === ApiModel.Device) as Device[];

    const documents = included.filter((object) => {
      switch (object.type) {
        case ApiModel.DocumentGeneric:
        case ApiModel.DocumentGovernmentId:
          return true;
        default:
          return false;
      }
    }) as Document[];

    dispatch(
      setInquiriesDetails({
        key: id,
        inquiries,
        devices,
        documents,
        inquiryTemplateVersions,
        inquiryVerifications: verifications,
        listItems,
        listMatches,
        lists,
        sessions,
        tags,
        templates: [...templates, ...inquiryTemplates],
      }),
    );
  };
};

export const loadCaseReports = ({
  id,
  isPollRequest = false,
}: {
  id: string;
  isPollRequest?: boolean;
}) => {
  return async (dispatch: Dispatch) => {
    const options: ApiOptions = {};
    if (isPollRequest) {
      options.headers = {
        'Persona-Skip-Log-User-Action': 'true',
      };
    }

    const reportsResponse = await SessionClientDashboard.get<ApiListResponse<Report>>(
      ApiEndpoint.Case,
      id,
      'report-objects',
      options,
    );

    const reports = reportsResponse.data;
    const relationshipMap = keyBy('id', reportsResponse.included);
    const included = reportsResponse.included || [];

    const reportTemplates = reports
      .flatMap((report) => {
        if (report.relationships['report-template']?.data?.id) {
          return relationshipMap[report.relationships['report-template']?.data?.id];
        }
      })
      .filter(Boolean) as ReportTemplate[];

    const tags = included.filter((object) => object.type === ApiModel.Tag) as Tag[];

    dispatch(setReportsDetails({ key: id, reports, reportTemplates, tags }));
  };
};

export const redactCase = (id: string) => {
  return async () => {
    await SessionClientDashboard.deleteResource<Case>(ApiEndpoint.Case, id);
  };
};

export const createCase = ({
  environmentId,
  caseTemplateId,
  accountIds = [],
  inquiryIds = [],
  reportIds = [],
  transactionIds = [],
  verificationIds = [],
}: {
  environmentId: string;
  caseTemplateId: string;
  accountIds?: string[];
  inquiryIds?: string[];
  reportIds?: string[];
  transactionIds?: string[];
  verificationIds?: string[];
}) => {
  return async () => {
    return await SessionClientDashboard.postResourceCreate<
      Case,
      ApiSingletonPayload<{
        'environment-id': string;
        'case-template-id': string;
        'account-ids': string[];
        'document-ids': string[];
        'inquiry-ids': string[];
        'report-ids': string[];
        'txn-ids': string[];
        'verification-ids': string[];
      }>
    >({
      type: ApiModel.Case,
      attributes: {
        'environment-id': environmentId,
        'case-template-id': caseTemplateId,
        'account-ids': accountIds,
        'document-ids': [],
        'inquiry-ids': inquiryIds,
        'report-ids': reportIds,
        'txn-ids': transactionIds,
        'verification-ids': verificationIds,
      },
    });
  };
};

export const refreshCaseQueryResults = (currentCaseId: string) => {
  return async () => {
    await SessionClientDashboard.postResourceAction<Case>(
      { id: currentCaseId, type: ApiModel.Case },
      'refresh-query-results',
    );
  };
};
